'use client';

import { Box, type SxProps, Typography, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useFooterSx } from './Footer';
import { TermsAndConditions } from '../modal/TermsAndConditions';
import { type ReactNode, useState } from 'react';

interface SeparatorProps {
	color: string;
	sx: SxProps;
}

const Separator = (props: SeparatorProps) => {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('md'));
	if (isMobile) return null;

	return (
		<Typography sx={props.sx} style={{ color: `${props.color}` }}>
			|
		</Typography>
	);
};

interface TextLinkWithModalTrigger {
	text: string;
	sx: SxProps;
	variant: string;
	setModalVisible: (state: boolean) => void;
}

const TextLinkWithModalTrigger = (props: TextLinkWithModalTrigger) => (
	<Typography
		style={{ padding: '0 5px', color: `${props.variant}` }}
		sx={props.sx}
		onClick={() => props.setModalVisible(true)}
	>
		{props.text}
	</Typography>
);

export const FooterTermsLinks = (props: {
	color: string;
	termsAndConditions?: string;
	privacyNotice?: string;
	customFAQModal?: (visible: boolean, onClose: () => void) => ReactNode;
}) => {
	const theme = useTheme();
	const sx = useFooterSx(props.color, theme);
	const [termsModalVisible, setTermsModalVisible] = useState(false);
	const [privacyModalVisible, setPrivacyModalVisible] = useState(false);
	const [faqModalVisible, setFaqModalVisible] = useState(false);
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

	const flexDirection = isMobile ? 'column' : 'row';

	return (
		<Box display="flex" width={1 / 3} flexDirection={flexDirection} alignItems="center" justifyContent="center">
			<Box
				display="flex"
				flexDirection={flexDirection}
				alignItems="center"
				flexWrap="nowrap"
				mt={isMobile ? '10px' : '0'}
				mb={isMobile ? '10px' : '0'}
			>
				<TextLinkWithModalTrigger
					text="Terms"
					setModalVisible={setTermsModalVisible}
					variant={props.color}
					sx={sx.text}
				/>
				<Separator color={props.color} sx={sx.text} />
			</Box>
			{props.customFAQModal && (
				<Box
					display="flex"
					flexDirection={`${isMobile ? 'column' : 'row'}`}
					alignItems="center"
					flexWrap="nowrap"
					mb={isMobile ? '10px' : '0'}
				>
					<TextLinkWithModalTrigger
						text="FAQ"
						setModalVisible={setFaqModalVisible}
						sx={sx.text}
						variant={props.color}
					/>
					<Separator color={props.color} sx={sx.text} />
				</Box>
			)}{' '}
			<Box padding="0 5px" mb={isMobile ? '10px' : '0'}>
				<TextLinkWithModalTrigger
					text="Privacy Policy"
					setModalVisible={setPrivacyModalVisible}
					sx={sx.text}
					variant={props.color}
				/>
			</Box>
			<TermsAndConditions
				textKey="termsAndConditions"
				open={termsModalVisible}
				handleClose={() => setTermsModalVisible(false)}
				withButton={false}
				text={props.termsAndConditions ?? ''}
			/>
			<TermsAndConditions
				textKey="privacyNotice"
				open={privacyModalVisible}
				handleClose={() => setPrivacyModalVisible(false)}
				withButton={false}
				text={props.privacyNotice ?? ''}
			/>
			{props.customFAQModal?.(faqModalVisible, () => setFaqModalVisible(false))}
		</Box>
	);
};
